// src/hooks.tsx
import { useCallback, useEffect, useRef, useState, useLayoutEffect } from "react";
var useSafeLayoutEffect = Boolean(globalThis == null ? void 0 : globalThis.document) ? useLayoutEffect : useEffect;
function useImage(props = {}) {
  const { loading, src, srcSet, onLoad, onError, crossOrigin, sizes, ignoreFallback } = props;
  const [status, setStatus] = useState("pending");
  useEffect(() => {
    setStatus(src ? "loading" : "pending");
  }, [src]);
  const imageRef = useRef();
  const load = useCallback(() => {
    if (!src)
      return;
    flush();
    const img = new Image();
    img.src = src;
    if (crossOrigin)
      img.crossOrigin = crossOrigin;
    if (srcSet)
      img.srcset = srcSet;
    if (sizes)
      img.sizes = sizes;
    if (loading)
      img.loading = loading;
    img.onload = (event) => {
      flush();
      setStatus("loaded");
      onLoad == null ? void 0 : onLoad(event);
    };
    img.onerror = (error) => {
      flush();
      setStatus("failed");
      onError == null ? void 0 : onError(error);
    };
    imageRef.current = img;
  }, [src, crossOrigin, srcSet, sizes, onLoad, onError, loading]);
  const flush = () => {
    if (imageRef.current) {
      imageRef.current.onload = null;
      imageRef.current.onerror = null;
      imageRef.current = null;
    }
  };
  useSafeLayoutEffect(() => {
    if (ignoreFallback)
      return void 0;
    if (status === "loading") {
      load();
    }
    return () => {
      flush();
    };
  }, [status, load, ignoreFallback]);
  return ignoreFallback ? "loaded" : status;
}
var shouldShowFallbackImage = (status, fallbackStrategy) => status !== "loaded" && fallbackStrategy === "beforeLoadOrError" || status === "failed" && fallbackStrategy === "onError";
var globalConfig = {};
var config = ({ breakPoints }) => {
  if (breakPoints) {
    globalConfig.breakPoints = breakPoints;
  }
};
var defaultBreakPoints = {
  sm: "(min-width: 640px)",
  md: "(min-width: 768px)",
  lg: "(min-width: 1024px)",
  xl: "(min-width: 1280px)",
  "2xl": "(min-width: 1536px)"
};
var getUesdDefaultUrl = (originDefaultUrl, usedBreakPoints) => {
  if (typeof originDefaultUrl === "string") {
    return originDefaultUrl;
  }
  const keys = Object.keys(originDefaultUrl);
  if (typeof window === "undefined") {
    return originDefaultUrl[keys[0]];
  }
  const usedKey = keys.find((key) => {
    const media = usedBreakPoints[key] || defaultBreakPoints[key] || key;
    return window && window.matchMedia(media);
  });
  return originDefaultUrl[usedKey || keys[0]];
};
var genetateResponsiveImageSourceConfig = (sourceConfig, usedBreakPoints, defaultUrl) => {
  const responsiveImageSourceConfig = sourceConfig && sourceConfig.map((currentItem) => {
    const mediaString = currentItem.media;
    return {
      media: usedBreakPoints[mediaString] || defaultBreakPoints[mediaString] || (currentItem == null ? void 0 : currentItem.media),
      srcSet: (currentItem == null ? void 0 : currentItem.srcSet) || defaultUrl,
      type: currentItem == null ? void 0 : currentItem.type,
      fallbackSrc: currentItem == null ? void 0 : currentItem.fallbackSrc
    };
  });
  return responsiveImageSourceConfig;
};
var useResponsiveImage = (responsiveImageProps) => {
  if (!responsiveImageProps) {
    return {
      showResponsiveImage: false
    };
  }
  const { sourceConfig, defaultUrl } = responsiveImageProps;
  const usedBreakPoints = globalConfig.breakPoints || {};
  const usedDefaultUrl = getUesdDefaultUrl(defaultUrl, usedBreakPoints);
  const responsiveImageSourceConfig = genetateResponsiveImageSourceConfig(
    sourceConfig,
    usedBreakPoints,
    usedDefaultUrl
  );
  const showResponsiveImage = !!usedDefaultUrl && responsiveImageSourceConfig && responsiveImageSourceConfig.length > 0;
  if (typeof window === "undefined") {
    return {
      responsiveImageSourceConfig,
      responsiveImageDefaultUrl: usedDefaultUrl,
      responsiveImageInitUrl: "",
      showResponsiveImage
    };
  }
  const initItem = responsiveImageSourceConfig.find((item) => {
    let mediaMatch = false;
    let typeMatch = true;
    if (!item.media) {
      mediaMatch = true;
    } else {
      mediaMatch = window && window.matchMedia(item.media).matches;
    }
    return mediaMatch && typeMatch;
  });
  const responsiveImageInitUrl = initItem == null ? void 0 : initItem.srcSet;
  return {
    responsiveImageSourceConfig,
    responsiveImageDefaultUrl: usedDefaultUrl,
    responsiveImageInitUrl,
    showResponsiveImage
  };
};
var useBgResponsiveImage = (responsiveImageProps) => {
  if (!responsiveImageProps) {
    return {
      showResponsiveImage: false
    };
  }
  const { sourceConfig, defaultUrl } = responsiveImageProps;
  const usedBreakPoints = globalConfig.breakPoints || {};
  const usedDefaultUrl = getUesdDefaultUrl(defaultUrl, usedBreakPoints);
  const responsiveImageSourceConfig = genetateResponsiveImageSourceConfig(
    sourceConfig,
    usedBreakPoints,
    usedDefaultUrl
  );
  const showResponsiveImage = !!usedDefaultUrl && responsiveImageSourceConfig && responsiveImageSourceConfig.length > 0;
  return {
    showResponsiveImage,
    responsiveImageDefaultUrl: usedDefaultUrl,
    responsiveImageSourceConfig
  };
};

export {
  useSafeLayoutEffect,
  useImage,
  shouldShowFallbackImage,
  config,
  defaultBreakPoints,
  useResponsiveImage,
  useBgResponsiveImage
};
//# sourceMappingURL=chunk-RDOW37CZ.esm.js.map